"use client"

import React, { useState } from "react";
import { X, Check, AlertCircle, FileJson } from 'lucide-react'
import { motion } from 'framer-motion'
import { toast } from 'react-toastify'


// Validate PAN number format
const validatePAN = (pan) => {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
};

// Parse Profile data from JSON
const parseProfileData = (jsonData, currentPAN) => {
  // console.log('Starting Profile data parsing...'); // Removed console logging
  
  const log = [];
  
  const appendLog = (message) => {
    log.push(message);
    // console.log(message); // Removed console logging
  };
  
  try {
    // Function to recursively search for profile data
    const findProfileData = (obj, path = '', visited = new Set()) => {
      if (visited.has(obj)) {
        return null;
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        // Check for personalInfo structure
        if (obj.personalInfo) {
          appendLog(`Found profile data at path: ${path}.personalInfo`);
          return obj.personalInfo;
        }
        
        // Check for assesseeName structure
        if (obj.assesseeName) {
          appendLog(`Found assessee data at path: ${path}.assesseeName`);
          return obj;
        }
        
        // Recursively search in nested objects
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const result = findProfileData(value, path ? `${path}.${key}` : key, visited);
            if (result) return result;
          }
        }
      }
      
      return null;
    };

    // Function to recursively search for bank details
    const findBankDetails = (obj, path = '', visited = new Set()) => {
      if (visited.has(obj)) {
        return null;
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        // Check for bankAccountDtls structure
        if (obj.bankAccountDtls && Array.isArray(obj.bankAccountDtls)) {
          appendLog(`Found bank details at path: ${path}.bankAccountDtls`);
          return obj.bankAccountDtls;
        }
        
        // Recursively search in nested objects
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const result = findBankDetails(value, path ? `${path}.${key}` : key, visited);
            if (result) return result;
          }
        }
      }
      
      return null;
    };

    // Function to recursively search for address details
    const findAddressDetails = (obj, path = '', visited = new Set()) => {
      if (visited.has(obj)) {
        return null;
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        // Check for address structure
        if (obj.address) {
          appendLog(`Found address details at path: ${path}.address`);
          return obj.address;
        }
        
        // Recursively search in nested objects
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const result = findAddressDetails(value, path ? `${path}.${key}` : key, visited);
            if (result) return result;
          }
        }
      }
      
      return null;
    };

    // Function to recursively search for other sources income data
    const findOtherSourcesData = (obj, path = '', visited = new Set()) => {
      if (visited.has(obj)) {
        return null;
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        // Check for scheduleOS structure
        if (obj.scheduleOS) {
          appendLog(`Found scheduleOS data at path: ${path}.scheduleOS`);
          return {
            scheduleOS: obj.scheduleOS,
            incomeDeductionsOthersInc: obj.incomeDeductionsOthersInc || []
          };
        }
        
        // Check for incomeDeductionsOthersInc structure
        if (obj.incomeDeductionsOthersInc && Array.isArray(obj.incomeDeductionsOthersInc)) {
          appendLog(`Found incomeDeductionsOthersInc data at path: ${path}.incomeDeductionsOthersInc`);
          return {
            scheduleOS: obj.scheduleOS || {},
            incomeDeductionsOthersInc: obj.incomeDeductionsOthersInc
          };
        }
        
        // Recursively search in nested objects
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const result = findOtherSourcesData(value, path ? `${path}.${key}` : key, visited);
            if (result) return result;
          }
        }
      }
      
      return null;
    };
    
    const profileData = findProfileData(jsonData);
    const bankDetails = findBankDetails(jsonData);
    const addressDetails = findAddressDetails(jsonData);
    const otherSourcesData = findOtherSourcesData(jsonData);
    
    if (!profileData) {
      appendLog('No profile data found in JSON');
      return { data: null, log: log.join('\n') };
    }
    
    // Extract profile information
    const assesseeName = profileData.assesseeName || profileData.assesseeVerName || {};
    const personalInfo = profileData.personalInfo || profileData;
    
    const firstName = String(assesseeName.firstName || '');
    const middleName = String(assesseeName.middleName || '');
    const lastName = String(assesseeName.surNameOrOrgName || '');
    const fullName = `${firstName} ${middleName} ${lastName}`.trim();
    
    const fatherName = String(personalInfo.fatherName || '');
    const dob = String(personalInfo.dob || '');
    const pan = String(personalInfo.pan || personalInfo.assesseVerPan || '');
    
    // Validate PAN
    if (!pan) {
      throw new Error('PAN number not found in JSON data');
    }
    
    if (!validatePAN(pan)) {
      throw new Error('Invalid PAN number format in JSON data');
    }
    
    // Check if PAN matches current user
    if (currentPAN && pan !== currentPAN) {
      throw new Error(`This JSON data does not belong to you. PAN in JSON (${pan}) does not match your PAN (${currentPAN})`);
    }
    
    // Extract bank details
    let bankInfo = null;
    if (bankDetails && bankDetails.length > 0) {
      const firstBank = bankDetails[0];
      if (firstBank.addtnlBankDetails && firstBank.addtnlBankDetails.length > 0) {
        const bankDetail = firstBank.addtnlBankDetails[0];
        // Map account type to valid enum values
        const mapAccountType = (accountType) => {
          const type = String(accountType || '').toUpperCase();
          const validTypes = ['SB', 'CA', 'CC', 'OD', 'NRO', 'OTH'];
          return validTypes.includes(type) ? type : 'SB'; // Default to SB if invalid
        };

        bankInfo = {
          bankName: String(bankDetail.bankName || ''),
          ifscCode: String(bankDetail.ifsccode || ''),
          accountNumber: String(bankDetail.bankAccountNo || ''),
          accountType: mapAccountType(bankDetail.AccountType),
          useForRefund: bankDetail.useForRefund === 'true' || bankDetail.useForRefund === true
        };
        appendLog(`Extracted bank details: Bank=${bankInfo.bankName}, IFSC=${bankInfo.ifscCode}, AccountType=${bankInfo.accountType}, UseForRefund=${bankInfo.useForRefund}`);
      }
    }
    
    // Map country code to valid enum values
    const mapCountryCode = (countryCode) => {
      const code = String(countryCode || '').replace('+', '');
      const validCountryCodes = [
        "93", "1001", "355", "213", "684", "376", "244", "1264", "1010", "1268", "54", "374", "297", "61", "43", "994", "1242", "973", "880", "1246", "375", "32", "501", "229", "1441", "975", "591", "1002", "387", "267", "1003", "55", "1014", "673", "359", "226", "257", "238", "855", "237", "1", "1345", "236", "235", "56", "86", "9", "672", "57", "270", "242", "243", "682", "506", "225", "385", "53", "1015", "357", "420", "45", "253", "1767", "1809", "593", "20", "503", "240", "291", "372", "251", "500", "298", "679", "358", "33", "594", "689", "1004", "241", "220", "995", "49", "233", "350", "30", "299", "1473", "590", "1671", "502", "1481", "224", "245", "592", "509", "1005", "6", "504", "852", "36", "354", "91", "62", "98", "964", "353", "1624", "972", "5", "1876", "81", "1534", "962", "7", "254", "686", "850", "82", "965", "996", "856", "371", "961", "266", "231", "218", "423", "370", "352", "853", "389", "261", "265", "60", "960", "223", "356", "692", "596", "222", "230", "269", "52", "691", "373", "377", "976", "382", "1664", "212", "258", "95", "264", "674", "977", "31", "687", "64", "505", "227", "234", "683", "15", "1670", "47", "968", "92", "680", "970", "507", "675", "595", "51", "63", "1011", "48", "14", "1787", "974", "262", "40", "8", "250", "1006", "290", "1869", "1758", "1007", "508", "1784", "685", "378", "239", "966", "221", "381", "248", "232", "65", "1721", "421", "386", "677", "252", "28", "1008", "211", "35", "94", "249", "597", "1012", "268", "46", "41", "963", "886", "992", "255", "66", "670", "228", "690", "676", "1868", "216", "90", "993", "1649", "688", "256", "380", "971", "44", "2", "1009", "598", "998", "678", "58", "84", "1284", "1340", "681", "1013", "967", "260", "263", "9999"
      ];
      return validCountryCodes.includes(code) ? code : "91"; // Default to India if invalid
    };

    // Map state code to valid enum values
    const mapStateCode = (stateCode) => {
      const code = String(stateCode || '').padStart(2, '0'); // Ensure 2 digits
      const validStateCodes = [
        "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31", "32", "33", "34", "35", "36", "37", "99"
      ];
      return validStateCodes.includes(code) ? code : "09"; // Default to Delhi if invalid
    };

    // Extract address details
    let addressInfo = null;
    if (addressDetails) {
      addressInfo = {
        residenceNo: String(addressDetails.residenceNo || ''),
        roadOrStreet: String(addressDetails.roadOrStreet || ''),
        localityOrArea: String(addressDetails.localityOrArea || ''),
        cityOrTownOrDistrict: String(addressDetails.cityOrTownOrDistrict || ''),
        stateCode: mapStateCode(addressDetails.stateCode),
        pinCode: String(addressDetails.pinCode || ''),
        zipCode: String(addressDetails.zipCode || ''),
        mobileNo: String(addressDetails.mobileNo || ''),
        countryCodeMobile: String(addressDetails.countryCodeMobile || ''),
        // Map country code to valid enum values
        countryCode: mapCountryCode(addressDetails.countryCode || addressDetails.countryCodeMobile || '91'),
        emailAddress: String(addressDetails.emailAddress || ''),
        emailAddressSecondary: String(addressDetails.emailAddressSecondary || ''),
        residenceName: String(addressDetails.residenceName || '')
      };
      appendLog(`Extracted address details: Road/Street=${addressInfo.roadOrStreet}, Country=${addressInfo.countryCode}, State=${addressInfo.stateCode}, Mobile=${addressInfo.mobileNo}, Email=${addressInfo.emailAddress}, PinCode=${addressInfo.pinCode}`);
    }

    // Extract other sources income data
    let otherSourcesInfo = null;
    if (otherSourcesData) {
      const { scheduleOS, incomeDeductionsOthersInc } = otherSourcesData;
      
      // Extract dividend data from scheduleOS
      let dividendGross = 0;
      if (scheduleOS.incOthThanOwnRaceHorse && scheduleOS.incOthThanOwnRaceHorse.dividendGross) {
        dividendGross = scheduleOS.incOthThanOwnRaceHorse.dividendGross;
      }
      
      // Extract data from incomeDeductionsOthersInc array
      let interestFromDeposit = 0;
      let interestFromSavings = 0;
      let dividendFromArray = 0;
      
      if (incomeDeductionsOthersInc && Array.isArray(incomeDeductionsOthersInc)) {
        incomeDeductionsOthersInc.forEach(item => {
          const amount = item.othSrcOthAmount || 0;
          const nature = item.othSrcNatureDesc || '';
          
          switch (nature) {
            case 'IFD': // Interest from Deposit
              interestFromDeposit = amount;
              break;
            case 'SAV': // Interest from Savings Bank Account
              interestFromSavings = amount;
              break;
            case 'DIV': // Dividend
              dividendFromArray = amount;
              break;
          }
        });
      }
      
      // Use dividend from array if available, otherwise from scheduleOS
      const finalDividend = dividendFromArray > 0 ? dividendFromArray : dividendGross;
      
      otherSourcesInfo = {
        interestFromDeposit: interestFromDeposit,
        interestFromSavings: interestFromSavings,
        dividend: finalDividend
      };
      
      appendLog(`Extracted other sources data: Interest from Deposit=${interestFromDeposit}, Interest from Savings=${interestFromSavings}, Dividend=${finalDividend}`);
    }
    
    const profileInfo = {
      firstName,
      middleName,
      lastName,
      fullName,
      fatherName,
      dob,
      pan,
      bankDetails: bankInfo,
      addressDetails: addressInfo,
      otherSourcesDetails: otherSourcesInfo
    };
    
    appendLog(`Extracted profile: Name=${fullName}, Father=${fatherName}, DOB=${dob}, PAN=${pan}`);
    
    return {
      data: profileInfo,
      log: log.join('\n')
    };
    
  } catch (error) {
    appendLog(`Profile parsing error: ${error.message}`);
    throw error;
  }
};

// Parse Tax Paid JSON data (reusing from comprehensive uploader)
const parseTaxPaidJSON = (jsonData) => {
  // console.log('Starting Tax Paid JSON parsing...'); // Removed console logging
  
  const results = [];
  const log = [];
  
  const appendLog = (message) => {
    log.push(message);
    // console.log(message); // Removed console logging
  };
  
  try {
    const findTaxPaidData = (obj, path = '', visited = new Set()) => {
      const found = [];
      
      if (visited.has(obj)) {
        return found;
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        if (obj.taxPayments && obj.taxPayments.taxPayment) {
          appendLog(`Found Tax Paid data at path: ${path}.taxPayments.taxPayment`);
          found.push(...obj.taxPayments.taxPayment);
        }
        
        if (Array.isArray(obj.taxPayment)) {
          appendLog(`Found Tax Paid data at path: ${path}.taxPayment`);
          found.push(...obj.taxPayment);
        }
        
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            found.push(...findTaxPaidData(value, path ? `${path}.${key}` : key, visited));
          }
        }
      }
      
      return found;
    };
    
    const allTaxPaidEntries = findTaxPaidData(jsonData);
    
    if (allTaxPaidEntries.length === 0) {
      appendLog('No Tax Paid data found in JSON');
      return { data: [], log: log.join('\n') };
    }
    
    appendLog(`Found ${allTaxPaidEntries.length} Tax Paid entries before deduplication`);
    
    const seenEntries = new Set();
    const taxPaidEntries = allTaxPaidEntries.filter(entry => {
      const key = `${entry.bsrCode || ''}_${entry.srlNoOfChaln || ''}_${entry.amt || 0}`;
      if (seenEntries.has(key)) {
        appendLog(`Removing duplicate Tax Paid entry: BSR=${entry.bsrCode}, Challan=${entry.srlNoOfChaln}, Amount=${entry.amt}`);
        return false;
      }
      seenEntries.add(key);
      return true;
    });
    
    appendLog(`After deduplication: ${taxPaidEntries.length} unique Tax Paid entries`);
    
    taxPaidEntries.forEach((entry, index) => {
      try {
        const bsrCode = entry.bsrCode || '';
        const challanNo = entry.srlNoOfChaln || '';
        const amount = entry.amt || 0;
        const dateDep = entry.dateDep || '';
        const bankName = entry.nameOfBankAndBranch?.nameOfBank || '';
        const branchName = entry.nameOfBankAndBranch?.nameOfBranch || '';
        const receiptNumber = entry.receiptNumber || '';
        
        if (!bsrCode || !challanNo || amount <= 0) {
          appendLog(`Skipping Tax Paid entry ${index + 1}: Missing required data (BSR: ${bsrCode}, Challan: ${challanNo}, Amount: ${amount})`);
          return;
        }
        
        const taxPaidEntry = {
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          fields: {
            dateOfDeposit: dateDep || '',
            bsrCode: bsrCode || '',
            challanNo: (challanNo || '').toString(),
            amount: (amount || 0).toString(),
            bankName: bankName || '',
            branchName: branchName || '',
            receiptNumber: receiptNumber || ''
          }
        };
        
        results.push(taxPaidEntry);
        appendLog(`Extracted Tax Paid entry ${index + 1}: BSR=${bsrCode}, Challan=${challanNo}, Amount=${amount}, Date=${dateDep}`);
        
      } catch (entryError) {
        appendLog(`Error processing Tax Paid entry ${index + 1}: ${entryError.message}`);
      }
    });
    
  } catch (error) {
    appendLog(`Tax Paid JSON parsing error: ${error.message}`);
  }
  
  return {
    data: results,
    log: log.join('\n')
  };
};

// Parse Other TDS JSON data (reusing from comprehensive uploader)
const parseOtherTDSJSON = (jsonData) => {
  // console.log('Starting Other TDS JSON parsing...'); // Removed console logging
  
  const results = [];
  const log = [];
  
  const appendLog = (message) => {
    log.push(message);
    // console.log(message); // Removed console logging
  };
  
  try {
    const findTDSData = (obj, path = '', visited = new Set()) => {
      const found = [];
      
      if (visited.has(obj)) {
        return found;
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        if (obj.tdsOnOthThanSals && obj.tdsOnOthThanSals.tdSonOthThanSal) {
          appendLog(`Found TDS data at path: ${path}.tdsOnOthThanSals.tdSonOthThanSal`);
          found.push(...obj.tdsOnOthThanSals.tdSonOthThanSal);
        }
        
        if (obj.form26as && obj.form26as.tdsOnOthThanSals && obj.form26as.tdsOnOthThanSals.tdSonOthThanSal) {
          appendLog(`Found TDS data at path: ${path}.form26as.tdsOnOthThanSals.tdSonOthThanSal`);
          found.push(...obj.form26as.tdsOnOthThanSals.tdSonOthThanSal);
        }
        
        if (Array.isArray(obj.tdSonOthThanSal)) {
          appendLog(`Found TDS data at path: ${path}.tdSonOthThanSal`);
          found.push(...obj.tdSonOthThanSal);
        }
        
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            found.push(...findTDSData(value, path ? `${path}.${key}` : key, visited));
          }
        }
      }
      
      return found;
    };
    
    const allTdsEntries = findTDSData(jsonData);
    
    if (allTdsEntries.length === 0) {
      appendLog('No TDS data found in JSON');
      return { data: [], log: log.join('\n') };
    }
    
    appendLog(`Found ${allTdsEntries.length} TDS entries before deduplication`);
    
    const seenEntries = new Set();
    const tdsEntries = allTdsEntries.filter(entry => {
      const key = `${entry.employerOrDeductorOrCollectDetl?.tan || ''}_${entry.sectionCode || ''}_${entry.grossAmount || 0}`;
      if (seenEntries.has(key)) {
        appendLog(`Removing duplicate TDS entry: TAN=${entry.employerOrDeductorOrCollectDetl?.tan}, Section=${entry.sectionCode}, Gross=${entry.grossAmount}`);
        return false;
      }
      seenEntries.add(key);
      return true;
    });
    
    appendLog(`After deduplication: ${tdsEntries.length} unique TDS entries`);
    
    tdsEntries.forEach((entry, index) => {
      try {
        const taxDeductCreditDtls = entry.taxDeductCreditDtls || {};
        const employerOrDeductorOrCollectDetl = entry.employerOrDeductorOrCollectDetl || {};
        
        const grossAmount = entry.grossAmount || 0;
        const tdsDeducted = taxDeductCreditDtls.taxDeductedOwnHands || 0;
        const tdsClaimed = taxDeductCreditDtls.taxClaimedOwnHands || 0;
        const tan = employerOrDeductorOrCollectDetl.tan || '';
        const name = employerOrDeductorOrCollectDetl.employerOrDeductorOrCollecterName || '';
        const section = entry.sectionCode || '';
        const headOfIncome = entry.headOfIncome || '';
        const tdsCreditName = entry.TDSCreditName || '';
        
        if (!tan || !name || grossAmount <= 0) {
          appendLog(`Skipping TDS entry ${index + 1}: Missing required data (TAN: ${tan}, Name: ${name}, Gross: ${grossAmount})`);
          return;
        }
        
        const tdsEntry = {
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          selfTDS: false,
          selfClaimed: false,
          fields: {
            tanOfDeductor: tan || '',
            name: (name || '').trim(),
            section: section || '',
            finYear: '',
            grossAmount: (grossAmount || 0).toString(),
            tds: (tdsDeducted || 0).toString(),
            panWhoseTDSClaimed: '',
            tdsClaimed: ((tdsClaimed || tdsDeducted) || 0).toString(),
            headOfIncome: headOfIncome || '',
            tdsCreditName: tdsCreditName || ''
          }
        };
        
        results.push(tdsEntry);
        appendLog(`Extracted TDS entry ${index + 1}: TAN=${tan}, Name=${name}, Section=${section}, Gross=${grossAmount}, TDS=${tdsDeducted}, TDSClaimed=${tdsClaimed}`);
        
      } catch (entryError) {
        appendLog(`Error processing TDS entry ${index + 1}: ${entryError.message}`);
      }
    });
    
  } catch (error) {
    appendLog(`TDS JSON parsing error: ${error.message}`);
  }
  
  return {
    data: results,
    log: log.join('\n')
  };
};

// Parse TCS JSON data
const parseTCSJSON = (jsonData) => {
  // console.log('Starting TCS JSON parsing...'); // Removed console logging
  
  const results = [];
  const log = [];
  
  const appendLog = (message) => {
    log.push(message);
    // console.log(message); // Removed console logging
  };
  
  try {
    // Function to recursively search for TCS data
    const findTCSData = (obj, path = '', visited = new Set()) => {
      if (visited.has(obj)) {
        return [];
      }
      
      if (obj && typeof obj === 'object') {
        visited.add(obj);
        
        // Check for scheduleTCS structure
        if (obj.scheduleTCS && obj.scheduleTCS.tcs && Array.isArray(obj.scheduleTCS.tcs)) {
          appendLog(`Found TCS data at path: ${path}.scheduleTCS.tcs`);
          return obj.scheduleTCS.tcs;
        }
        
        // Check for form26as structure
        if (obj.form26as && obj.form26as.scheduleTCS && obj.form26as.scheduleTCS.tcs && Array.isArray(obj.form26as.scheduleTCS.tcs)) {
          appendLog(`Found TCS data at path: ${path}.form26as.scheduleTCS.tcs`);
          return obj.form26as.scheduleTCS.tcs;
        }
        
        // Recursively search in nested objects
        const found = [];
        for (const [key, value] of Object.entries(obj)) {
          if (typeof value === 'object' && value !== null) {
            const nestedResults = findTCSData(value, path ? `${path}.${key}` : key, visited);
            found.push(...nestedResults);
          }
        }
        return found;
      }
      
      return [];
    };
    
    const allTcsEntries = findTCSData(jsonData);
    
    console.log('TCS parsing - allTcsEntries found:', allTcsEntries);
    
    if (allTcsEntries.length === 0) {
      appendLog('No TCS data found in JSON');
      console.log('No TCS data found in JSON');
      return { data: [], log: log.join('\n') };
    }
    
    appendLog(`Found ${allTcsEntries.length} TCS entries before deduplication`);
    
    const seenEntries = new Set();
    const tcsEntries = allTcsEntries.filter(entry => {
      const key = `${entry.employerOrDeductorOrCollectDetl?.tan || ''}_${entry.employerOrDeductorOrCollectDetl?.employerOrDeductorOrCollecterName || ''}_${entry.amtTCSClaimedThisYear || ''}`;
      if (seenEntries.has(key)) {
        return false;
      }
      seenEntries.add(key);
      return true;
    });
    
    appendLog(`After deduplication: ${tcsEntries.length} TCS entries`);
    
    // Process each TCS entry
    tcsEntries.forEach((entry, index) => {
      try {
        const tan = entry.employerOrDeductorOrCollectDetl?.tan || '';
        const name = entry.employerOrDeductorOrCollectDetl?.employerOrDeductorOrCollecterName || '';
        const amount = entry.amtfrm26AS || 0;
        const tcsClaimed = entry.amtTCSClaimedThisYear || 0;
        const tcsCreditName = entry.TCSCreditName || '';
        
        const tcsEntry = {
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          selfTDS: false,
          selfClaimed: false,
          fields: {
            tanOfCollector: tan || '',
            name: (name || '').trim(),
            finYear: '',
            tcs: (tcsClaimed || 0).toString(), // Map amtTCSClaimedThisYear to TCS field
            panWhoseTCSClaimed: '',
            tcsClaimed: (tcsClaimed || 0).toString(), // Map amtTCSClaimedThisYear to TCS Claimed field
            tcsCreditName: tcsCreditName || ''
          }
        };
        
        results.push(tcsEntry);
        appendLog(`Extracted TCS entry ${index + 1}: TAN=${tan}, Name=${name}, TCS=${tcsClaimed}, TCSClaimed=${tcsClaimed}`);
        console.log(`TCS Entry ${index + 1} processed:`, tcsEntry);
        
      } catch (entryError) {
        appendLog(`Error processing TCS entry ${index + 1}: ${entryError.message}`);
        console.error(`Error processing TCS entry ${index + 1}:`, entryError);
      }
    });
    
    console.log('Final TCS results:', results);
    
  } catch (error) {
    appendLog(`TCS JSON parsing error: ${error.message}`);
  }
  
  return {
    data: results,
    log: log.join('\n')
  };
};

const ProjectLevelJSONUploader = ({ isVisible = true, hideUploadInterface = false }) => {
  const [extractedData, setExtractedData] = useState({
    profile: null,
    taxPaid: [],
    otherTDS: [],
    tcs: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [processingLog, setProcessingLog] = useState('');

  const handleFileUpload = async (file) => {
    setError(null);
    setLoading(true);
    
    try {
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size too large. Please upload a JSON file smaller than 5MB.');
      }

      if (!file.name.toLowerCase().endsWith('.json')) {
        throw new Error('Please upload a valid JSON file (.json extension required).');
      }

      const text = await file.text();
      let jsonData;
      
      try {
        jsonData = JSON.parse(text);
      } catch (parseError) {
        throw new Error('Invalid JSON format. Please ensure the file contains valid JSON data.');
      }

      // Get current PAN from localStorage for validation
      const currentPAN = localStorage.getItem("PAN_NO");

      // Parse all types of data
      const profileResult = parseProfileData(jsonData, currentPAN);
      const taxPaidResult = parseTaxPaidJSON(jsonData);
      const otherTDSResult = parseOtherTDSJSON(jsonData);
      const tcsResult = parseTCSJSON(jsonData);

      // Combine all logs
      const allLogs = [
        '=== PROFILE PARSING ===',
        profileResult.log,
        '\n=== TAX PAID PARSING ===',
        taxPaidResult.log,
        '\n=== OTHER TDS PARSING ===',
        otherTDSResult.log,
        '\n=== TCS PARSING ===',
        tcsResult.log
      ].filter(log => log && log.trim()).join('\n');

      setProcessingLog(allLogs);

      // Use data directly from parsing functions (already formatted)
      const formattedData = {
        profile: profileResult.data,
        taxPaid: taxPaidResult.data,
        otherTDS: otherTDSResult.data,
        tcs: tcsResult.data
      };

      setExtractedData(formattedData);

      const totalEntries = formattedData.taxPaid.length + formattedData.otherTDS.length + formattedData.tcs.length;
      const hasProfile = formattedData.profile !== null;

      if (totalEntries > 0 || hasProfile) {
        // Store data in localStorage
        if (hasProfile) {
          localStorage.setItem("prefill_profile", JSON.stringify(formattedData.profile));
          localStorage.setItem("FIRST_NAME", formattedData.profile.firstName);
          localStorage.setItem("MIDDLE_NAME", formattedData.profile.middleName);
          localStorage.setItem("LAST_NAME", formattedData.profile.lastName);
          localStorage.setItem("FATHER_NAME", formattedData.profile.fatherName);
          localStorage.setItem("DOB", formattedData.profile.dob);
          localStorage.setItem("PAN_NO", formattedData.profile.pan.toUpperCase());
        }

        if (formattedData.taxPaid.length > 0) {
          // console.log('Storing tax paid data:', formattedData.taxPaid); // Removed console logging
          localStorage.setItem("prefill_tax_paid", JSON.stringify(formattedData.taxPaid));
        }

        if (formattedData.otherTDS.length > 0) {
          // console.log('Storing other TDS data:', formattedData.otherTDS); // Removed console logging
          localStorage.setItem("prefill_other_tds", JSON.stringify(formattedData.otherTDS));
        }

        if (formattedData.tcs.length > 0) {
          console.log('Storing TCS data:', formattedData.tcs);
          localStorage.setItem("prefill_tcs", JSON.stringify(formattedData.tcs));
        } else {
          console.log('No TCS data to store:', formattedData.tcs);
        }

        // Store other sources data if available
        if (formattedData.profile && formattedData.profile.otherSourcesDetails) {
          // console.log('Storing other sources data:', formattedData.profile.otherSourcesDetails); // Removed console logging
          localStorage.setItem("prefill_other_sources", JSON.stringify(formattedData.profile.otherSourcesDetails));
        }

        // Trigger a custom event to notify other components
        window.dispatchEvent(new CustomEvent('prefillDataUpdated', {
          detail: formattedData
        }));
        
        // Show success toast
        toast.success('Data uploaded successfully!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            fontSize: '12px',
            padding: '8px 12px',
            minHeight: 'auto'
          }
        });
      } else {
        const errorMsg = 'No valid profile, TDS, or Tax Paid data found in the JSON file. Please ensure the file contains the expected structure.';
        setError(errorMsg);
        // Show error toast
        toast.error(errorMsg, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            fontSize: '12px',
            padding: '8px 12px',
            minHeight: 'auto'
          }
        });
      }
    } catch (err) {
      console.error('JSON parse error:', err);
      setError(err.message);
      // Show error toast
      toast.error(err.message, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          fontSize: '12px',
          padding: '8px 12px',
          minHeight: 'auto'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      handleFileUpload(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file && file.name.toLowerCase().endsWith('.json')) {
      setUploadedFile(file);
      handleFileUpload(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };


  if (!isVisible) return null;

  const totalEntries = extractedData.taxPaid.length + extractedData.otherTDS.length;
  const hasProfile = extractedData.profile !== null;

  return (
    <div className="mb-4">
      {/* Upload Area - Hidden when hideUploadInterface is true */}
      {!hideUploadInterface && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="flex items-center justify-center space-x-2 mb-2">
            <FileJson className="w-6 h-6 text-gray-500" />
            <span className="text-lg font-medium text-gray-700">Upload PreFill Data</span>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            Drop JSON here or{' '}
            <label className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium">
              browse
              <input
                type="file"
                accept=".json"
                onChange={handleFileInput}
                className="hidden"
                disabled={loading}
              />
            </label>
          </div>
          <div className="text-xs text-gray-500">
            Supports: Profile data, Tax Paid, TDS on Salary, Other TDS, Other TDS2, and TCS data
          </div>
          {uploadedFile && (
            <div className="mt-2 text-xs text-gray-500">
              {uploadedFile.name} ({Math.round(uploadedFile.size / 1024)} KB)
            </div>
          )}
        </div>
      )}

      {/* Hidden file input for when interface is hidden */}
      {hideUploadInterface && (
        <input
          type="file"
          accept=".json"
          onChange={handleFileInput}
          className="hidden"
          disabled={loading}
        />
      )}

      {/* Loading State */}
      {loading && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700"
        >
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span>Parsing JSON and validating data, please wait...</span>
          </div>
        </motion.div>
      )}

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
            {uploadedFile && (
              <button
                onClick={() => handleFileUpload(uploadedFile)}
                disabled={loading}
                className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50 ml-2"
              >
                {loading ? 'Retrying...' : 'Retry'}
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Success Message */}
      {(totalEntries > 0 || hasProfile) && !loading && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700"
        >
          <div className="flex items-center space-x-2">
            <Check className="w-4 h-4" />
            <span>Data uploaded successfully</span>
          </div>
        </motion.div>
      )}

      {/* Processing Log - Hidden as requested */}
      {/* {processingLog && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-800">Processing Log</h4>
            <button
              onClick={() => setProcessingLog('')}
              className="text-blue-600 hover:text-blue-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <textarea
            readOnly
            value={processingLog}
            className="w-full p-2 border border-blue-300 rounded text-xs resize-none bg-white"
            rows={8}
            placeholder="Processing log will appear here..."
          />
        </motion.div>
      )} */}


    </div>
  );
};

export default ProjectLevelJSONUploader;
